import Navigation from "@/components/landing/Navigation";
import Footer from "@/components/landing/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { MapPin, Clock, DollarSign, Users, X, ChevronRight } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "@/hooks/useTranslation";

const Careers = () => {
  const { t, language } = useTranslation();
  const [isFlutterModalOpen, setIsFlutterModalOpen] = useState(false);
  const [isPhpModalOpen, setIsPhpModalOpen] = useState(false);
  const openPositions = [{
    title: "Google Flutter Developer",
    description: "Build cross-platform mobile applications for healthcare using Flutter framework and Dart programming language.",
    skills: ["Flutter", "Dart", "Mobile Development"],
    location: "Remote",
    type: "Full-time",
    gradient: "from-primary to-secondary",
    isClickable: true
  }, {
    title: "PHP/Laravel Developer",
    description: "Develop robust backend systems and APIs for healthcare applications using PHP and Laravel framework.",
    skills: ["PHP", "Laravel", "MySQL"],
    location: "Hybrid",
    type: "Full-time",
    gradient: "from-secondary to-accent-teal",
    isClickable: true
  }];
  return <div className="min-h-screen bg-gradient-to-br from-neutral-100 to-neutral-50 relative overflow-hidden">
      {/* Flowing background elements */}
      <div className="absolute top-0 right-0 w-1/2 h-[60%] bg-gradient-to-bl from-secondary/15 via-secondary/8 to-transparent blur-3xl" />
      <div className="absolute top-1/4 left-0 w-1/3 h-[50%] bg-gradient-to-r from-accent-teal/12 via-accent-teal/6 to-transparent blur-2xl" />
      <div className="absolute bottom-0 right-1/4 w-1/2 h-[40%] bg-gradient-to-t from-secondary/10 via-transparent to-transparent blur-3xl" />
      
      <Navigation />
      <main className="pt-32 pb-16 relative z-10">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8
          }} className="text-center mb-16">
              <h1 className="heading-xl mb-6 py-[10px] text-accent-teal">
                {t.careers.title}
              </h1>
              <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto leading-relaxed py-[2px]">
                {t.careers.subtitle}
              </p>
            </motion.div>
            
            <div className="max-w-none">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} className="relative bg-white/80 backdrop-blur-sm border border-primary/10 rounded-2xl p-8 mb-12 overflow-hidden">
                {/* Background decorative elements */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-secondary/3 to-accent-teal/5" />
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary/10 to-transparent rounded-full blur-2xl" />
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-secondary/10 to-transparent rounded-full blur-xl" />
                
                {/* Content wrapper */}
                <div className="relative z-10">
                <h2 className="heading-lg mb-6 py-[10px]">{t.careers.whyWorkAtVidavo.title}</h2>
                <div className="space-y-6">
                  <p className="text-muted-foreground text-lg leading-relaxed py-[2px]">
                    {t.careers.whyWorkAtVidavo.description}
                  </p>
                  
                  <div className="border-t border-border pt-6">
                    <h3 className="text-xl font-semibold mb-4 text-primary py-[2px]">{t.careers.whyWorkAtVidavo.culture.title}</h3>
                    <p className="text-muted-foreground leading-relaxed mb-4 py-[2px]">
                      {t.careers.whyWorkAtVidavo.culture.description}
                    </p>
                    
                    <ul className="grid md:grid-cols-2 gap-3 mt-4">
                      <li className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-gradient-to-r from-primary to-secondary rounded-full mt-2 flex-shrink-0" />
                        <span className="text-muted-foreground">{t.careers.whyWorkAtVidavo.culture.values.innovation}</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-gradient-to-r from-primary to-secondary rounded-full mt-2 flex-shrink-0" />
                        <span className="text-muted-foreground">{t.careers.whyWorkAtVidavo.culture.values.learning}</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-gradient-to-r from-primary to-secondary rounded-full mt-2 flex-shrink-0" />
                        <span className="text-muted-foreground">{t.careers.whyWorkAtVidavo.culture.values.collaboration}</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-gradient-to-r from-primary to-secondary rounded-full mt-2 flex-shrink-0" />
                        <span className="text-muted-foreground">{t.careers.whyWorkAtVidavo.culture.values.impact}</span>
                      </li>
                     </ul>
                   </div>
                 </div>
                </div>
               </motion.div>
               
               {/* No Open Positions Section */}
               <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.3
            }} className="relative bg-white/80 backdrop-blur-sm border border-primary/10 rounded-2xl p-8 mb-12 overflow-hidden">
                 {/* Background decorative elements */}
                 <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 to-accent-teal/5" />
                 <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-secondary/10 to-transparent rounded-full blur-2xl" />
                 <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-accent-teal/10 to-transparent rounded-full blur-xl" />
                 
                 {/* Content wrapper */}
                 <div className="relative z-10 text-center">
                 
                  <p className="text-muted-foreground text-lg leading-relaxed mb-8 max-w-3xl mx-auto py-[2px]">
                   {t.careers.noOpenPositions.description} <a href="https://www.linkedin.com/company/vidavo-s.a./" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary/80 underline transition-colors">{t.careers.noOpenPositions.linkedIn}</a> {t.careers.noOpenPositions.descriptionEnd}
                  </p>
                 
                 <Button asChild className="button-primary group mb-4">
                    <a href="mailto:info@vidavo.eu">{t.careers.noOpenPositions.sendEmail}</a>
                  </Button>
                 </div>
               </motion.div>
              
              <motion.h2 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.4
            }} className="heading-lg mb-8 text-center py-[10px] hidden">
                Open Positions
              </motion.h2>
              
              <div className="space-y-6 mb-12 hidden">
                {openPositions.map((position, index) => <motion.div key={position.title} initial={{
                opacity: 0,
                y: 30
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.6,
                delay: 0.6 + index * 0.1
              }}>
                    {position.isClickable ? <Dialog open={position.title === "Google Flutter Developer" ? isFlutterModalOpen : isPhpModalOpen} onOpenChange={position.title === "Google Flutter Developer" ? setIsFlutterModalOpen : setIsPhpModalOpen}>
                        <DialogTrigger asChild>
                          <Card className="healthcare-card group h-full cursor-pointer hover:shadow-lg transition-all duration-300">
                            <div className={`absolute inset-0 bg-gradient-to-br ${position.gradient} opacity-5 group-hover:opacity-10 transition-opacity duration-500`} />
                            
                            <CardHeader className="relative z-10">
                              <div className="flex items-center justify-between mb-2">
                                <CardTitle className="text-xl group-hover:text-primary transition-colors duration-300">
                                  {position.title}
                                </CardTitle>
                                <Button variant="outline" size="sm" className="ml-2 bg-background/50 border-primary/30 hover:bg-primary/10 hover:border-primary/50">
                                  View Details
                                  <ChevronRight className="w-4 h-4 ml-1" />
                                </Button>
                              </div>
                              <p className="text-muted-foreground leading-relaxed">
                                {position.description}
                              </p>
                            </CardHeader>
                            
                            <CardContent className="relative z-10 space-y-4">
                              <div className="flex flex-wrap gap-2">
                                {position.skills.map((skill, skillIndex) => <span key={skillIndex} className="px-3 py-1 bg-gradient-to-r from-primary/10 to-secondary/10 text-primary text-sm rounded-full font-medium border border-primary/20">
                                    {skill}
                                  </span>)}
                              </div>
                              
                              <div className="flex items-center justify-between pt-4 border-t border-border">
                                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                  <div className="flex items-center gap-1">
                                    <MapPin className="w-4 h-4" />
                                    {position.location}
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <Clock className="w-4 h-4" />
                                    {position.type}
                                  </div>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        </DialogTrigger>
                        
                        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-background border border-border">
                          <DialogHeader>
                            <DialogTitle className="text-2xl font-bold text-primary mb-4">
                              {position.title}
                            </DialogTitle>
                          </DialogHeader>
                          
                          {position.title === "Google Flutter Developer" ? <div className="space-y-8 text-foreground">
                            {/* Job Overview */}
                            <div className="bg-gradient-to-r from-primary/5 to-secondary/5 p-6 rounded-lg border border-primary/20">
                              <h3 className="text-lg font-semibold mb-3 text-primary">Position Overview</h3>
                              <p className="text-muted-foreground">
                                Build cross-platform mobile applications for healthcare using Flutter framework and Dart programming language.
                              </p>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                <div className="flex items-center gap-2">
                                  <MapPin className="w-4 h-4 text-primary" />
                                  <span className="text-sm">Remote</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Clock className="w-4 h-4 text-primary" />
                                  <span className="text-sm">Full-time</span>
                                </div>
                              </div>
                            </div>

                            {/* Responsibilities */}
                            <div>
                              <h3 className="text-xl font-semibold mb-4 text-primary border-b border-border pb-2">
                                Responsibilities
                              </h3>
                              <ul className="space-y-3">
                                <li className="flex items-start gap-3">
                                  <div className="w-2 h-2 bg-gradient-to-r from-primary to-secondary rounded-full mt-2 flex-shrink-0" />
                                  <span>Enhance Vida24© by actively crafting and implementing innovative features that elevate its functionality and user experience.</span>
                                </li>
                                <li className="flex items-start gap-3">
                                  <div className="w-2 h-2 bg-gradient-to-r from-primary to-secondary rounded-full mt-2 flex-shrink-0" />
                                  <span>Engage in architecture design and the development of scalable software solutions.</span>
                                </li>
                                <li className="flex items-start gap-3">
                                  <div className="w-2 h-2 bg-gradient-to-r from-primary to-secondary rounded-full mt-2 flex-shrink-0" />
                                  <span>Identify and address any bugs and performance bottlenecks, ensuring seamless functionality and optimal performance.</span>
                                </li>
                              </ul>
                            </div>

                            {/* Requirements */}
                            <div>
                              <h3 className="text-xl font-semibold mb-4 text-primary border-b border-border pb-2">
                                Requirements
                              </h3>
                              <ul className="space-y-3">
                                <li className="flex items-start gap-3">
                                  <div className="w-2 h-2 bg-gradient-to-r from-primary to-secondary rounded-full mt-2 flex-shrink-0" />
                                  <span>Strong understanding of the Flutter framework (previously published flutter apps will be considered as a plus)</span>
                                </li>
                                <li className="flex items-start gap-3">
                                  <div className="w-2 h-2 bg-gradient-to-r from-primary to-secondary rounded-full mt-2 flex-shrink-0" />
                                  <span>Capable of producing clean code, technical documentation, and refining existing code.</span>
                                </li>
                                <li className="flex items-start gap-3">
                                  <div className="w-2 h-2 bg-gradient-to-r from-primary to-secondary rounded-full mt-2 flex-shrink-0" />
                                  <span>Good knowledge of Dart programming language and design patterns</span>
                                </li>
                                <li className="flex items-start gap-3">
                                  <div className="w-2 h-2 bg-gradient-to-r from-primary to-secondary rounded-full mt-2 flex-shrink-0" />
                                  <span>Familiarity with Bloc, http, drift, freezed packages is advantageous</span>
                                </li>
                                <li className="flex items-start gap-3">
                                  <div className="w-2 h-2 bg-gradient-to-r from-primary to-secondary rounded-full mt-2 flex-shrink-0" />
                                  <span>Strong debugging skills</span>
                                </li>
                                <li className="flex items-start gap-3">
                                  <div className="w-2 h-2 bg-gradient-to-r from-primary to-secondary rounded-full mt-2 flex-shrink-0" />
                                  <span>Being self-motivated, passionate, and enthusiastic</span>
                                </li>
                              </ul>
                            </div>

                            {/* Nice to Have */}
                            <div>
                              <h3 className="text-xl font-semibold mb-4 text-primary border-b border-border pb-2">
                                Nice to Have
                              </h3>
                              <ul className="space-y-3">
                                <li className="flex items-start gap-3">
                                  <div className="w-2 h-2 bg-gradient-to-r from-accent-teal to-accent-orange rounded-full mt-2 flex-shrink-0" />
                                  <span>Experience in native iOS/Android development will be considered a plus</span>
                                </li>
                                <li className="flex items-start gap-3">
                                  <div className="w-2 h-2 bg-gradient-to-r from-accent-teal to-accent-orange rounded-full mt-2 flex-shrink-0" />
                                  <span>Knowledge of Kotlin and involvement on WearOS applications</span>
                                </li>
                                <li className="flex items-start gap-3">
                                  <div className="w-2 h-2 bg-gradient-to-r from-accent-teal to-accent-orange rounded-full mt-2 flex-shrink-0" />
                                  <span>Experienced with versioning and source control systems such as Bitbucket, GitHub etc.</span>
                                </li>
                                <li className="flex items-start gap-3">
                                  <div className="w-2 h-2 bg-gradient-to-r from-accent-teal to-accent-orange rounded-full mt-2 flex-shrink-0" />
                                  <span>Experience on administration of mobile applications on the stores (App Store, Google Play)</span>
                                </li>
                                <li className="flex items-start gap-3">
                                  <div className="w-2 h-2 bg-gradient-to-r from-accent-teal to-accent-orange rounded-full mt-2 flex-shrink-0" />
                                  <span>Experience with customizing flutter plugins based on the needs of the project</span>
                                </li>
                                <li className="flex items-start gap-3">
                                  <div className="w-2 h-2 bg-gradient-to-r from-accent-teal to-accent-orange rounded-full mt-2 flex-shrink-0" />
                                  <span>Working in agile (scrum) methodology</span>
                                </li>
                              </ul>
                            </div>

                            {/* Benefits */}
                            <div>
                              <h3 className="text-xl font-semibold mb-4 text-primary border-b border-border pb-2">
                                Benefits
                              </h3>
                              <ul className="space-y-3">
                                <li className="flex items-start gap-3">
                                  <div className="w-2 h-2 bg-gradient-to-r from-secondary to-primary rounded-full mt-2 flex-shrink-0" />
                                  <span>Ideal Work Environment</span>
                                </li>
                                <li className="flex items-start gap-3">
                                  <div className="w-2 h-2 bg-gradient-to-r from-secondary to-primary rounded-full mt-2 flex-shrink-0" />
                                  <span>Paid training and certifications</span>
                                </li>
                                <li className="flex items-start gap-3">
                                  <div className="w-2 h-2 bg-gradient-to-r from-secondary to-primary rounded-full mt-2 flex-shrink-0" />
                                  <span>Attendance at conferences and events</span>
                                </li>
                                <li className="flex items-start gap-3">
                                  <div className="w-2 h-2 bg-gradient-to-r from-secondary to-primary rounded-full mt-2 flex-shrink-0" />
                                  <span>Competitive compensation package</span>
                                </li>
                                <li className="flex items-start gap-3">
                                  <div className="w-2 h-2 bg-gradient-to-r from-secondary to-primary rounded-full mt-2 flex-shrink-0" />
                                  <span>Private Health Insurance</span>
                                </li>
                                <li className="flex items-start gap-3">
                                  <div className="w-2 h-2 bg-gradient-to-r from-secondary to-primary rounded-full mt-2 flex-shrink-0" />
                                  <span>Work from home flexibility</span>
                                </li>
                              </ul>
                            </div>

                            {/* Instructions for submitting CV */}
                            <div className="bg-gradient-to-r from-accent-teal/5 to-primary/5 p-6 rounded-lg border border-accent-teal/20">
                              <h3 className="text-xl font-semibold mb-4 text-primary border-b border-border pb-2">
                                Instructions for Submitting a CV
                              </h3>
                              <p className="text-muted-foreground mb-4">
                                We're excited to learn about your background and experience! Please ensure your CV includes:
                              </p>
                              <ul className="space-y-2 mb-6">
                                <li className="flex items-start gap-3">
                                  <div className="w-2 h-2 bg-gradient-to-r from-accent-teal to-primary rounded-full mt-2 flex-shrink-0" />
                                  <span>Clear contact information and professional summary</span>
                                </li>
                                <li className="flex items-start gap-3">
                                  <div className="w-2 h-2 bg-gradient-to-r from-accent-teal to-primary rounded-full mt-2 flex-shrink-0" />
                                  <span>Detailed work experience with Flutter/mobile development projects</span>
                                </li>
                                <li className="flex items-start gap-3">
                                  <div className="w-2 h-2 bg-gradient-to-r from-accent-teal to-primary rounded-full mt-2 flex-shrink-0" />
                                  <span>Education background and relevant certifications</span>
                                </li>
                                <li className="flex items-start gap-3">
                                  <div className="w-2 h-2 bg-gradient-to-r from-accent-teal to-primary rounded-full mt-2 flex-shrink-0" />
                                  <span>Portfolio links or examples of published Flutter applications</span>
                                </li>
                              </ul>
                              
                              <button className="button-primary group w-full md:w-auto" onClick={() => {
                          window.location.href = 'mailto:info@vidavo.eu?subject=Flutter Developer Application - Vidavo&body=Dear Vidavo Team,%0D%0A%0D%0AI am interested in the Google Flutter Developer position. Please find my CV attached.%0D%0A%0D%0ABest regards,';
                          setIsFlutterModalOpen(false);
                        }}>
                                <Users className="w-5 h-5 mr-2" />
                                Apply for Flutter Position
                              </button>
                            </div>
                          </div> : <div className="space-y-8 text-foreground">
                            {/* Job Overview */}
                            <div className="bg-gradient-to-r from-secondary/5 to-accent-teal/5 p-6 rounded-lg border border-secondary/20">
                              <h3 className="text-lg font-semibold mb-3 text-primary">Position Overview</h3>
                              <p className="text-muted-foreground">
                                Develop robust backend systems and APIs for healthcare applications using PHP and Laravel framework.
                              </p>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                <div className="flex items-center gap-2">
                                  <MapPin className="w-4 h-4 text-primary" />
                                  <span className="text-sm">Hybrid</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Clock className="w-4 h-4 text-primary" />
                                  <span className="text-sm">Full-time</span>
                                </div>
                              </div>
                            </div>

                            {/* Responsibilities */}
                            <div>
                              <h3 className="text-xl font-semibold mb-4 text-primary border-b border-border pb-2">
                                Responsibilities
                              </h3>
                              <ul className="space-y-3">
                                <li className="flex items-start gap-3">
                                  <div className="w-2 h-2 bg-gradient-to-r from-secondary to-accent-teal rounded-full mt-2 flex-shrink-0" />
                                  <span>Enhance Vida24© by actively crafting and implementing innovative features that elevate its functionality and user experience.</span>
                                </li>
                                <li className="flex items-start gap-3">
                                  <div className="w-2 h-2 bg-gradient-to-r from-secondary to-accent-teal rounded-full mt-2 flex-shrink-0" />
                                  <span>Engage in architecture design and the development of scalable software solutions.</span>
                                </li>
                                <li className="flex items-start gap-3">
                                  <div className="w-2 h-2 bg-gradient-to-r from-secondary to-accent-teal rounded-full mt-2 flex-shrink-0" />
                                  <span>Identify and address any bugs and performance bottlenecks, ensuring seamless functionality and optimal performance.</span>
                                </li>
                              </ul>
                            </div>

                            {/* Requirements */}
                            <div>
                              <h3 className="text-xl font-semibold mb-4 text-primary border-b border-border pb-2">
                                Requirements
                              </h3>
                              <ul className="space-y-3">
                                <li className="flex items-start gap-3">
                                  <div className="w-2 h-2 bg-gradient-to-r from-secondary to-accent-teal rounded-full mt-2 flex-shrink-0" />
                                  <span>Bachelor's degree in engineering, Information Technology, or related fields.</span>
                                </li>
                                <li className="flex items-start gap-3">
                                  <div className="w-2 h-2 bg-gradient-to-r from-secondary to-accent-teal rounded-full mt-2 flex-shrink-0" />
                                  <span>Proven experience with PHP Laravel</span>
                                </li>
                                <li className="flex items-start gap-3">
                                  <div className="w-2 h-2 bg-gradient-to-r from-secondary to-accent-teal rounded-full mt-2 flex-shrink-0" />
                                  <span>Demonstrable knowledge of web technologies including HTML, CSS, Javascript, AJAX etc</span>
                                </li>
                                <li className="flex items-start gap-3">
                                  <div className="w-2 h-2 bg-gradient-to-r from-secondary to-accent-teal rounded-full mt-2 flex-shrink-0" />
                                  <span>Good knowledge of version control tools system and a CI workflow</span>
                                </li>
                                <li className="flex items-start gap-3">
                                  <div className="w-2 h-2 bg-gradient-to-r from-secondary to-accent-teal rounded-full mt-2 flex-shrink-0" />
                                  <span>Good knowledge of relational databases (e.g. MySQL) and NoSQL databases (e.g. MongoDB)</span>
                                </li>
                                <li className="flex items-start gap-3">
                                  <div className="w-2 h-2 bg-gradient-to-r from-secondary to-accent-teal rounded-full mt-2 flex-shrink-0" />
                                  <span>Display self-initiative and a collaborative mindset within teams.</span>
                                </li>
                              </ul>
                            </div>

                            {/* Additional Experience */}
                            <div>
                              <h3 className="text-xl font-semibold mb-4 text-primary border-b border-border pb-2">
                                Additional Experience (Plus)
                              </h3>
                              <ul className="space-y-3">
                                <li className="flex items-start gap-3">
                                  <div className="w-2 h-2 bg-gradient-to-r from-accent-teal to-accent-orange rounded-full mt-2 flex-shrink-0" />
                                  <span>Experience with Agile Scrum methodology</span>
                                </li>
                                <li className="flex items-start gap-3">
                                  <div className="w-2 h-2 bg-gradient-to-r from-accent-teal to-accent-orange rounded-full mt-2 flex-shrink-0" />
                                  <span>Experience with Atlassian Jira</span>
                                </li>
                                <li className="flex items-start gap-3">
                                  <div className="w-2 h-2 bg-gradient-to-r from-accent-teal to-accent-orange rounded-full mt-2 flex-shrink-0" />
                                  <span>Experience with Elastic search and Docker technologies</span>
                                </li>
                                <li className="flex items-start gap-3">
                                  <div className="w-2 h-2 bg-gradient-to-r from-accent-teal to-accent-orange rounded-full mt-2 flex-shrink-0" />
                                  <span>Good knowledge of English (written and verbal)</span>
                                </li>
                              </ul>
                            </div>

                            {/* Benefits */}
                            <div>
                              <h3 className="text-xl font-semibold mb-4 text-primary border-b border-border pb-2">
                                Benefits
                              </h3>
                              <ul className="space-y-3">
                                <li className="flex items-start gap-3">
                                  <div className="w-2 h-2 bg-gradient-to-r from-secondary to-accent-teal rounded-full mt-2 flex-shrink-0" />
                                  <span>Ideal Work Environment</span>
                                </li>
                                <li className="flex items-start gap-3">
                                  <div className="w-2 h-2 bg-gradient-to-r from-secondary to-accent-teal rounded-full mt-2 flex-shrink-0" />
                                  <span>Paid training and certifications</span>
                                </li>
                                <li className="flex items-start gap-3">
                                  <div className="w-2 h-2 bg-gradient-to-r from-secondary to-accent-teal rounded-full mt-2 flex-shrink-0" />
                                  <span>Attendance at conferences and events</span>
                                </li>
                                <li className="flex items-start gap-3">
                                  <div className="w-2 h-2 bg-gradient-to-r from-secondary to-accent-teal rounded-full mt-2 flex-shrink-0" />
                                  <span>Competitive compensation package</span>
                                </li>
                                <li className="flex items-start gap-3">
                                  <div className="w-2 h-2 bg-gradient-to-r from-secondary to-accent-teal rounded-full mt-2 flex-shrink-0" />
                                  <span>Private Health Insurance</span>
                                </li>
                                <li className="flex items-start gap-3">
                                  <div className="w-2 h-2 bg-gradient-to-r from-secondary to-accent-teal rounded-full mt-2 flex-shrink-0" />
                                  <span>Work from home flexibility</span>
                                </li>
                              </ul>
                            </div>

                            {/* Instructions for submitting CV */}
                            <div className="bg-gradient-to-r from-secondary/5 to-accent-teal/5 p-6 rounded-lg border border-secondary/20">
                              <h3 className="text-xl font-semibold mb-4 text-primary border-b border-border pb-2">
                                Instructions for Submitting a CV
                              </h3>
                              <p className="text-muted-foreground mb-4">
                                We're excited to learn about your background and experience! Please ensure your CV includes:
                              </p>
                              <ul className="space-y-2 mb-6">
                                <li className="flex items-start gap-3">
                                  <div className="w-2 h-2 bg-gradient-to-r from-secondary to-accent-teal rounded-full mt-2 flex-shrink-0" />
                                  <span>Clear contact information and professional summary</span>
                                </li>
                                <li className="flex items-start gap-3">
                                  <div className="w-2 h-2 bg-gradient-to-r from-secondary to-accent-teal rounded-full mt-2 flex-shrink-0" />
                                  <span>Detailed work experience with PHP/Laravel development projects</span>
                                </li>
                                <li className="flex items-start gap-3">
                                  <div className="w-2 h-2 bg-gradient-to-r from-secondary to-accent-teal rounded-full mt-2 flex-shrink-0" />
                                  <span>Education background and relevant certifications</span>
                                </li>
                                <li className="flex items-start gap-3">
                                  <div className="w-2 h-2 bg-gradient-to-r from-secondary to-accent-teal rounded-full mt-2 flex-shrink-0" />
                                  <span>Portfolio links or examples of web applications you've developed</span>
                                </li>
                              </ul>
                              
                              <button className="button-primary group w-full md:w-auto" onClick={() => {
                          window.location.href = 'mailto:info@vidavo.eu?subject=PHP/Laravel Developer Application - Vidavo&body=Dear Vidavo Team,%0D%0A%0D%0AI am interested in the PHP/Laravel Developer position. Please find my CV attached.%0D%0A%0D%0ABest regards,';
                          setIsPhpModalOpen(false);
                        }}>
                                <Users className="w-5 h-5 mr-2" />
                                Apply for PHP Position
                              </button>
                            </div>
                          </div>}
                        </DialogContent>
                      </Dialog> : <Card className="healthcare-card group h-full">
                        <div className={`absolute inset-0 bg-gradient-to-br ${position.gradient} opacity-5 group-hover:opacity-10 transition-opacity duration-500`} />
                        
                        <CardHeader className="relative z-10">
                          <CardTitle className="text-xl mb-2 group-hover:text-primary transition-colors duration-300">
                            {position.title}
                          </CardTitle>
                          <p className="text-muted-foreground leading-relaxed">
                            {position.description}
                          </p>
                        </CardHeader>
                        
                        <CardContent className="relative z-10 space-y-4">
                          <div className="flex flex-wrap gap-2">
                            {position.skills.map((skill, skillIndex) => <span key={skillIndex} className="px-3 py-1 bg-gradient-to-r from-primary/10 to-secondary/10 text-primary text-sm rounded-full font-medium border border-primary/20">
                                {skill}
                              </span>)}
                          </div>
                          
                          <div className="flex items-center justify-between pt-4 border-t border-border">
                            <div className="flex items-center gap-4 text-sm text-muted-foreground">
                              <div className="flex items-center gap-1">
                                <MapPin className="w-4 h-4" />
                                {position.location}
                              </div>
                              <div className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {position.type}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>}
                  </motion.div>)}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>;
};
export default Careers;